import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { InView } from 'react-intersection-observer'

/** `<RenderInView>` is a component used to render React components based on their proximity to the viewport (or a provided ref)
 *
 * `rush add -p @pcln/render-in-view --caret`
 *
 * `npm i --save @pcln/render-in-view`
 */
class RenderInView extends Component {
  constructor(props) {
    super(props)

    this.state = {
      shouldRender: false,
    }
  }

  static propTypes = {
    /** always render its children, regardless of relation to the viewport
     *
     *  this could be useful for DEFAULT state in SETI tests, or as an opt-out strategy for priority ui
     */
    alwaysRender: PropTypes.bool,

    /** '0px' or css '0px 0px 0px 0px' (top, right, buttom, left)
     *
     * margin within the root container (window if rootRef is not provided)
     *
     * for example: `rootMargin: '200px 0 500px 0'` would cause the component to render when it reaches 200px above the top of the viewport or 500px below the bottom
     */
    rootMargin: PropTypes.string,

    /** window will be the root element if not specified
     *
     * the current element of a forwarded React ref (https://reactjs.org/docs/react-api.html#reactcreateref) used with rootMargin to determine if the component should be rendered
     *
     * this might be useful for an element with fixed dimensions and `overflow: scroll;`
     */
    rootRef: PropTypes.object,

    /** element to use for the RenderInView wrapper */
    asElement: PropTypes.string,

    /** callback executed after render. only called once per instance of RenderInView */
    onRender: PropTypes.func,

    /** React children to be conditionally rendered */
    children: PropTypes.node.isRequired,

    /** enables styling RenderInView with `styled-components`
     *
     * `const Listing = styled(RenderInView)`
     */
    className: PropTypes.string,
  }

  static defaultProps = {
    alwaysRender: false,
    rootMargin: '0px 0px 0px 0px',
    asElement: 'span',
  }

  onInView = (isInView) => {
    if (isInView) {
      const { onRender } = this.props

      this.setState(() => {
        if (onRender && typeof onRender === 'function') {
          onRender()
        }

        return {
          shouldRender: true,
        }
      })
    }
  }

  render() {
    const { alwaysRender, rootMargin, rootRef, asElement, children, className } = this.props
    const { shouldRender } = this.state

    if (alwaysRender) {
      return children
    }

    return (
      <InView
        as={asElement}
        className={className}
        onChange={this.onInView}
        root={rootRef}
        rootMargin={rootMargin}
        triggerOnce
      >
        {shouldRender && children}
      </InView>
    )
  }
}

export default RenderInView
